import { useState } from 'react'
import { supabase } from './supabase'
import { SmsComposeModal } from './SmsComposeModal'
import { StatusReasonModal } from './StatusReasonModal'
import { ClientBadges } from './ClientBadges'
import { makeActivityEntry, makeAuditEntry, buildComplianceState } from './compliance'

const LEAD_STATUSES = ['New Lead', 'In Progress', 'Quote Sent', 'Awaiting Client', 'Under Referral', 'No Contact 1', 'No Contact 2', 'No Contact 3', 'Policy Placed', 'Not Interested']
const REASON_REQUIRED_STATUSES = ['Not Interested']
const STATUS_COLORS = {
'New Lead': '#6b8278',
  'In Progress': '#e6a817',
  'Quote Sent': '#3b82f6',
  'Awaiting Client': '#e6a817',
  'Under Referral': '#8b5cf6',
  'No Contact 1': '#f97316',
  'No Contact 2': '#ea580c',
  'No Contact 3': '#c2410c',
  'Policy Placed': '#1e3d30',
  'Not Interested': '#dc3545',
}
const SMS_TRIGGER_STATUSES = ['Quote Sent', 'No Contact 1', 'No Contact 2', 'No Contact 3', 'Policy Placed', 'Awaiting Client']
const BROKERS = ['Ben Rogers', 'Jesse Roach', 'Mitch Speer']

const normaliseAuPhone = (phone) => {
  const raw = (phone || '').replace(/\s+/g, '').trim()
  if (!raw) return ''
  if (raw.startsWith('+')) return raw
  if (raw.startsWith('61')) return `+${raw}`
  if (raw.startsWith('0')) return `+61${raw.slice(1)}`
  return raw
}

function LeadStatusNote({ lead, showToast, loadAll }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(lead.status_note || '')

  const save = async () => {
    const trimmed = val.trim()
    if (trimmed === (lead.status_note || '')) { setEditing(false); return }
    await supabase.from('clients').update({ status_note: trimmed || null }).eq('id', lead.id)
    setEditing(false)
    await loadAll()
  }

  if (editing) {
    return (
      <input
        autoFocus
        className="form-input"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
        onClick={e => e.stopPropagation()}
        placeholder="e.g. Quote sent, follow up 2 Aug..."
        style={{ fontSize: 11, padding: '3px 8px', marginTop: 4, width: '100%', maxWidth: 340 }}
      />
    )
  }

  return (
    <div
      onClick={e => { e.stopPropagation(); setEditing(true) }}
      style={{ fontSize: 11, color: lead.status_note ? 'var(--muted)' : 'rgba(107,130,120,0.4)', marginTop: 3, cursor: 'pointer', fontStyle: lead.status_note ? 'normal' : 'italic', lineHeight: 1.4 }}
    >
      {lead.status_note || '+ Add note'}
    </div>
  )
}

export function Leads({ leads, openClient, loadAll, showToast, openNewLead, user, FACT_FIND_OPTIONS }) {
  const [filter, setFilter] = useState('active')
  const [brokerFilter, setBrokerFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date_desc')
  const [policyModal, setPolicyModal] = useState(null)
  const [policyForm, setPolicyForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [smsPrompt, setSmsPrompt] = useState(null)
  const [reasonModal, setReasonModal] = useState(null)

  // Filter
  let displayed = filter === 'active'
    ? leads.filter((lead) => lead.lead_status !== 'Not Interested')
    : filter === 'not_interested'
      ? leads.filter((lead) => lead.lead_status === 'Not Interested')
      : leads

  // Broker filter
  if (brokerFilter !== 'all') {
    displayed = displayed.filter((lead) => lead.assigned_broker === brokerFilter)
  }

  // Sort
  displayed = [...displayed].sort((a, b) => {
    if (sortBy === 'date_desc') return new Date(b.created_at) - new Date(a.created_at)
    if (sortBy === 'date_asc') return new Date(a.created_at) - new Date(b.created_at)
    if (sortBy === 'status') return (a.lead_status || '').localeCompare(b.lead_status || '')
    if (sortBy === 'broker') return (a.assigned_broker || '').localeCompare(b.assigned_broker || '')
    if (sortBy === 'name') return (a.company_name || '').localeCompare(b.company_name || '')
    return 0
  })

  const handleStatusChange = async (lead, newStatus) => {
    const compliance = buildComplianceState(lead)

    if (newStatus === 'Quote Sent' && !compliance.important_info_sent_at) {
      showToast('Important Information is not logged on this file yet')
    }

    if (newStatus === 'Policy Placed') {
      if (!compliance.important_info_sent_at) return showToast('Record Important Information before placing the policy')
      if (!compliance.tmd_checked_at) return showToast('Record the TMD check before placing the policy')
      if (compliance.is_claims_made && !compliance.claims_made_notice_at) return showToast('Record the claims-made notice before placing the policy')
      if (compliance.informed_consent_required && !compliance.informed_consent_received_at) return showToast('Retail personal advice file - informed consent must be recorded before placement')

      const oneYearOut = new Date()
      oneYearOut.setFullYear(oneYearOut.getFullYear() + 1)
      setPolicyForm({
        policy_type: lead.fact_finds?.[0]?.type || lead.policy_type || '',
        start_date: new Date().toISOString().split('T')[0],
        renewal_date: oneYearOut.toISOString().split('T')[0],
      })
      setPolicyModal({ lead, newStatus })
      return
    }

    if (REASON_REQUIRED_STATUSES.includes(newStatus)) {
      setReasonModal({ lead, newStatus })
      return
    }

    await doStatusChange(lead, newStatus, null)
  }

  const doStatusChange = async (lead, newStatus, reason) => {
    setReasonModal(null)
    const { error: statusError } = await supabase.from('clients').update({ lead_status: newStatus }).eq('id', lead.id)
    if (statusError) {
      showToast(`⚠️ ${statusError.message || 'Error updating status'}`)
      return
    }

    try {
      const changes = [`Status: "${lead.lead_status || '—'}" -> "${newStatus}"`]
      if (reason) changes.push(`Reason: "${reason}"`)
      await supabase.from('clients').update({
        status_reason: reason || null,
        audit_log: [makeAuditEntry('status', `Status changed to ${newStatus}`, user.name, changes), ...(lead.audit_log || [])],
        activity_log: [makeActivityEntry('status', `Status changed to ${newStatus} by ${user.name}${reason ? ` - ${reason}` : ''}`, '🔄'), ...(lead.activity_log || [])],
      }).eq('id', lead.id)
    } catch (error) {
      console.warn('Log update failed:', error)
    }

    await loadAll()
    if (SMS_TRIGGER_STATUSES.includes(newStatus) && lead.phone) {
      setSmsPrompt({ lead, status: newStatus })
    }
  }

const handleSmsFromLeads = async (body, alsoEmail, emailSubject) => {
    if (!smsPrompt?.lead?.phone || !body.trim()) return
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-sms`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: normaliseAuPhone(smsPrompt.lead.phone),
          body: body.trim(),
          client_id: smsPrompt.lead.id,
          client_name: smsPrompt.lead.company_name,
          broker_name: user?.name,
        }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'SMS failed')

      // Optionally also send a branded email
      let emailSent = false
      if (alsoEmail && smsPrompt.lead.email) {
        try {
          const emailRes = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-client-email`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: smsPrompt.lead.email.trim(),
              subject: emailSubject,
              bodyText: body.trim(),
              clientName: smsPrompt.lead.company_name,
              brokerName: user?.name,
              client_id: smsPrompt.lead.id,
            }),
          })
          const emailResult = await emailRes.json()
          if (!emailRes.ok || !emailResult.success) throw new Error(emailResult.error || 'Email failed')
          emailSent = true
        } catch (emailErr) {
          showToast(`SMS sent, but email failed — ${emailErr.message}`)
        }
      }

      const logText = emailSent ? 'SMS + email sent' : 'SMS sent'
      const logIcon = emailSent ? '📧' : '📱'
      await supabase.from('clients').update({
        activity_log: [makeActivityEntry('sms', `${logText} by ${user.name}: "${body.trim().substring(0, 60)}"`, logIcon), ...(smsPrompt.lead.activity_log || [])],
        audit_log: [makeAuditEntry('sms', `${logText} to client`, user.name, [`To: ${smsPrompt.lead.phone}`, ...(emailSent ? [`Email: ${smsPrompt.lead.email}`] : [])]), ...(smsPrompt.lead.audit_log || [])],
      }).eq('id', smsPrompt.lead.id)

      if (emailSent) showToast(`SMS + email sent to ${smsPrompt.lead.company_name}`)
      else showToast(`SMS sent to ${smsPrompt.lead.company_name}`)
      await loadAll()
    } catch (error) {
      showToast(`SMS failed - ${error.message || 'check logs'}`)
    }
    setSmsPrompt(null)
  }

  const confirmPolicyPlaced = async () => {
    if (!policyForm.renewal_date) return showToast('Renewal date is required')
    setSaving(true)
    await supabase.from('clients').update({
      lead_status: 'Policy Placed',
      policy_type: policyForm.policy_type,
      policy_start_date: policyForm.start_date || null,
      renewal_date: policyForm.renewal_date,
      audit_log: [makeAuditEntry('status', 'Policy placed', user.name, [`Renewal: ${policyForm.renewal_date}`]), ...(policyModal.lead.audit_log || [])],
      activity_log: [makeActivityEntry('status', `Policy placed by ${user.name}`, '🎉'), ...(policyModal.lead.activity_log || [])],
    }).eq('id', policyModal.lead.id)
    await loadAll()
    setSaving(false)
    setPolicyModal(null)
    setPolicyForm({})
    showToast(`${policyModal.lead.company_name} moved to clients`)
  }

  return (
    <>
      <div className="section-head">
        <div>
          <div className="section-title">Leads</div>
          <div className="section-sub">{leads.filter((l) => l.lead_status !== 'Not Interested').length} active leads</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['active', 'Active'], ['not_interested', 'Not Interested'], ['all', 'All']].map(([value, label]) => (
            <button key={value} className={`btn btn-sm ${filter === value ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(value)}>{label}</button>
          ))}
          <button className="btn btn-orange btn-sm" onClick={openNewLead}>+ New Lead</button>
        </div>
      </div>

      {/* Filter & Sort Bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Broker</span>
          <select
            className="form-select"
            style={{ width: 'auto', fontSize: 12, padding: '5px 10px' }}
            value={brokerFilter}
            onChange={(e) => setBrokerFilter(e.target.value)}
          >
            <option value="all">All brokers</option>
            {BROKERS.map((b) => <option key={b} value={b}>{b.split(' ')[0]}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.4px' }}>Sort</span>
          <select
            className="form-select"
            style={{ width: 'auto', fontSize: 12, padding: '5px 10px' }}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date_desc">Newest first</option>
            <option value="date_asc">Oldest first</option>
            <option value="status">Status</option>
            <option value="broker">Broker</option>
            <option value="name">Name A–Z</option>
          </select>
        </div>
        <span style={{ fontSize: 12, color: 'var(--muted)', marginLeft: 'auto' }}>{displayed.length} lead{displayed.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="card">
        <div className="tbl-header" style={{ gridTemplateColumns: '2.2fr 1.5fr 1fr 1fr 80px' }}>
          <span>Lead</span>
          <span>Status</span>
          <span>Broker</span>
          <span>Added</span>
          <span></span>
        </div>
        {displayed.length === 0
          ? <div className="empty"><div className="empty-icon">🎯</div><div className="empty-title">No leads here</div><div className="empty-sub">Click "+ New Lead" to get started</div></div>
          : displayed.map((lead) => (
            <div key={lead.id} className="tbl-row" style={{ gridTemplateColumns: '2.2fr 1.5fr 1fr 1fr 80px' }}>
              <div className="cell-main" onClick={() => openClient(lead)}>
                <div className="cell-av">{lead.company_name?.substring(0, 2).toUpperCase()}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{lead.company_name}</div>
                  {lead.referring_partner && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', marginTop: 4, padding: '3px 8px', borderRadius: 999, background: 'rgba(242,101,34,0.12)', border: '1px solid rgba(242,101,34,0.28)', color: 'var(--orange)', fontSize: 11, fontWeight: 700 }}>
                      Referral: {lead.referring_partner}
                    </div>
                  )}
<ClientBadges client={lead} />
                  <LeadStatusNote lead={lead} showToast={showToast} loadAll={loadAll} />
                </div>
              </div>
              <div>
                <select
                  style={{ background: `${STATUS_COLORS[lead.lead_status] || '#6b8278'}15`, border: `1.5px solid ${STATUS_COLORS[lead.lead_status] || '#6b8278'}50`, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: STATUS_COLORS[lead.lead_status] || '#6b8278', cursor: 'pointer', outline: 'none', fontFamily: 'var(--font)', width: '100%' }}
                  value={lead.lead_status || 'New Lead'}
                  onChange={(event) => handleStatusChange(lead, event.target.value)}
                >
                  {LEAD_STATUSES.map((status) => <option key={status}>{status}</option>)}
                </select>
              </div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{lead.assigned_broker?.split(' ')[0] || '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}</div>
              <button className="btn btn-secondary btn-sm" onClick={() => openClient(lead)}>Open</button>
            </div>
          ))}
      </div>

      {reasonModal && <StatusReasonModal status={reasonModal.newStatus} onConfirm={(reason) => doStatusChange(reasonModal.lead, reasonModal.newStatus, reason)} onSkip={() => doStatusChange(reasonModal.lead, reasonModal.newStatus, null)} onCancel={() => setReasonModal(null)} />}
      {smsPrompt && <SmsComposeModal client={smsPrompt.lead} user={user} onSend={handleSmsFromLeads} onClose={() => setSmsPrompt(null)} sending={false} initialTemplate={smsPrompt.status} />}

      {policyModal && (
        <div className="overlay" onClick={(event) => event.target === event.currentTarget && setPolicyModal(null)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-head">
              <div>
                <div className="modal-title">Policy Placed</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{policyModal.lead.company_name}</div>
              </div>
              <button className="modal-close" onClick={() => setPolicyModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="full">
                  <label className="form-label">Policy type</label>
                  <select className="form-select" value={policyForm.policy_type || ''} onChange={(event) => setPolicyForm((current) => ({ ...current, policy_type: event.target.value }))}>
                    <option value="">Select cover type...</option>
                    {(FACT_FIND_OPTIONS || []).map((type) => <option key={type}>{type}</option>)}
                  </select>
                </div>
                <div><label className="form-label">Policy start date</label><input className="form-input" type="date" value={policyForm.start_date || ''} onChange={(event) => setPolicyForm((current) => ({ ...current, start_date: event.target.value }))} /></div>
                <div><label className="form-label">Renewal date *</label><input className="form-input" type="date" value={policyForm.renewal_date || ''} onChange={(event) => setPolicyForm((current) => ({ ...current, renewal_date: event.target.value }))} /></div>
              </div>
              <div className="form-actions">
                <button className="btn btn-secondary" onClick={() => setPolicyModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={confirmPolicyPlaced} disabled={saving || !policyForm.renewal_date}>{saving ? 'Saving...' : 'Confirm placement ->'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
